import React, { useState } from "react";
import { connect } from "react-redux";
import { Link, withRouter, RouteComponentProps } from "react-router-dom";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { routes } from "../routes";
import userService from "../../services/userService";
import { ILoginRequest } from "../../interfaces/user";
import JF_LOGO from "../../assets/images/JF Horizontal Color.png";
import { error as notificationError } from "../../utils/notification";

const LoginSchema = Yup.object().shape({
  email: Yup.string().email("Invalid email address").required("Email is required"),
  password: Yup.string().required("Password is required"),
});

interface IProps extends RouteComponentProps {
  login: (request: ILoginRequest, resolve?: Function, reject?: Function) => Promise<any>;
  loginWithFirebase: (request: ILoginRequest) => Promise<any>;
}

const DeveloperPortalLogin: React.FC<IProps> = ({ login, loginWithFirebase, history }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (values: any) => {
    setIsSubmitting(true);
    try {
      const res = await loginWithFirebase({ username: values.email, password: values.password });
      
      if (res && res.code === "auth/multi-factor-auth-required") {
        notificationError({
          title: "MFA Required",
          description: "Multi-factor authentication is required for your account. Please use the standard login portal."
        });
        setIsSubmitting(false);
        return;
      }

      await login({ username: values.email, password: values.password });
      history.push(routes.DEVELOPER_PORTAL_DASHBOARD);
    } catch (err) {
      console.error("Login failed", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="developer-portal-auth">
      <div className="auth-card">
        <div className="brand-logo">
          <img src={JF_LOGO} alt="Journey Foods" />
        </div>
        
        <div className="auth-header">
          <h1>Developer Portal</h1>
          <p>Sign in to manage your API integrations, review consumption logs, and access 2B+ data points.</p>
        </div>

        <Formik
          initialValues={{ email: "", password: "" }}
          validationSchema={LoginSchema}
          onSubmit={handleSubmit}
        >
          {({ errors, touched, isValid, dirty }) => (
            <Form className="auth-form">
              <div className="form-group">
                <label htmlFor="email">Work Email</label>
                <div className="input-wrapper">
                  <Field
                    type="email"
                    id="email"
                    name="email"
                    placeholder="name@company.com"
                    className={errors.email && touched.email ? "is-invalid" : ""}
                  />
                </div>
                <ErrorMessage name="email" component="div" className="error-message" />
              </div>

              <div className="form-group">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <label htmlFor="password">Password</label>
                  <Link to={routes.FORGOT_PASSWORD} style={{ fontSize: '13px', fontWeight: 600, color: '#17C9DC', textDecoration: 'none', marginBottom: '8px' }}>
                    Forgot password?
                  </Link>
                </div>
                <div className="input-wrapper">
                  <Field
                    type="password"
                    id="password"
                    name="password"
                    placeholder="••••••••"
                    className={errors.password && touched.password ? "is-invalid" : ""}
                  />
                </div>
                <ErrorMessage name="password" component="div" className="error-message" />
              </div>

              <button
                type="submit"
                className="auth-submit-btn"
                disabled={!isValid || !dirty || isSubmitting}
              >
                {isSubmitting ? (
                  <span>
                    <i className="fa fa-spinner fa-spin spinner"></i> Authenticating...
                  </span>
                ) : (
                  "Sign In to Developer Portal"
                )}
              </button>
            </Form>
          )}
        </Formik>

        <div className="auth-footer">
          New to the portal? <Link to={routes.DEVELOPER_PORTAL_SIGNUP}>Create developer account</Link>
        </div>
      </div>
    </div>
  );
};

const mapDispatchToProps = (dispatch: any) => ({
  login: (request: ILoginRequest, resolve?: Function, reject?: Function) =>
    dispatch(userService.login(request.username, request.password, resolve, reject)),
  loginWithFirebase: (request: ILoginRequest) =>
    dispatch(userService.loginWithFirebase(request.username, request.password)),
});

export default withRouter(connect(null, mapDispatchToProps)(DeveloperPortalLogin) as any);
